import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMe, type CreateJobPayload } from "@/hooks/use-queries";
import { api, type Job } from "@/lib/api";

export interface JobPostingInput {
  payload: CreateJobPayload;
  agentId?: string;
}
interface PendingPost extends JobPostingInput {
  ownerId: string;
  key: string;
  revised?: boolean;
}
export interface PostedJob {
  job: Job;
  agentId?: string;
  recovered?: boolean;
}

export function useJobPosting() {
  const { data: me } = useMe();
  const ownerId = me?.id;
  const cache = useQueryClient();
  const pending = useRef<PendingPost | null>(null);
  const [recovery, setRecovery] = useState<PendingPost | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const editing = useRef(false);
  const storageKey = ownerId ? `moltjobs:pending-job:${ownerId}` : null;

  useEffect(() => {
    pending.current = null;
    setRecovery(null);
    setCanEdit(false);
    editing.current = false;
    if (!storageKey || !ownerId) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw) as PendingPost;
      if (
        saved.ownerId !== ownerId ||
        typeof saved.key !== "string" ||
        !saved.payload?.title ||
        !saved.payload?.templateId
      )
        return;
      pending.current = saved;
      setRecovery(saved);
    } catch {
      /* A blocked or unavailable session store does not prevent posting. */
    }
  }, [storageKey, ownerId]);

  const mutation = useMutation({
    mutationFn: async (
      input: JobPostingInput | undefined
    ): Promise<PostedJob> => {
      if (!me || !storageKey) throw new Error("Sign in before posting a job.");
      const prior = pending.current;
      if (prior && input && !editing.current)
        throw new Error(
          "Check the previous posting attempt before creating another job."
        );
      if (!prior && !input)
        throw new Error("There is no pending posting attempt.");
      const request: PendingPost =
        prior && !input
          ? prior
          : {
              ...input!,
              ownerId: me.id,
              key: prior?.key ?? crypto.randomUUID(),
              ...(prior && { revised: true }),
            };
      if (request.ownerId !== me.id)
        throw new Error("Sign in with the account that posted this job.");
      pending.current = request;
      editing.current = false;
      setCanEdit(false);
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(request));
      } catch {
        /* Keep the in-memory request identity. */
      }
      const finish = (job: Job, recovered = false): PostedJob => {
        if (pending.current?.key === request.key) pending.current = null;
        setRecovery((current) =>
          current?.key === request.key ? null : current
        );
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          /* Storage may be unavailable. */
        }
        void cache.invalidateQueries({ queryKey: ["my-jobs"] });
        void cache.invalidateQueries({ queryKey: ["jobs"] });
        return {
          job,
          agentId: recovered && request.revised ? undefined : request.agentId,
          recovered,
        };
      };
      const lookup = async () =>
        (
          await api.get<{
            data: { state: "CREATED" | "NOT_FOUND"; job: Job | null };
          }>(`/jobs/creation-requests/${encodeURIComponent(request.key)}`)
        ).data.data;
      try {
        if (prior) {
          const existing = await lookup();
          if (existing.state === "CREATED" && existing.job)
            return finish(existing.job, true);
        }
        const job = (
          await api.post<{ data: Job }>("/jobs", request.payload, {
            headers: { "Idempotency-Key": request.key },
          })
        ).data.data;
        return finish(job);
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response
          ?.status;
        if (
          !prior &&
          status &&
          status >= 400 &&
          status < 500 &&
          status !== 408 &&
          status !== 429
        ) {
          pending.current = null;
          try {
            sessionStorage.removeItem(storageKey);
          } catch {
            /* Storage may be unavailable. */
          }
        } else {
          if (status === 409) {
            try {
              const existing = await lookup();
              if (existing.state === "CREATED" && existing.job)
                return finish(existing.job, true);
            } catch {
              /* Keep the single attempt until its result can be read. */
            }
          }
          setRecovery(request);
          setCanEdit(
            !!status &&
              status >= 400 &&
              status < 500 &&
              status !== 408 &&
              status !== 429
          );
        }
        throw error;
      }
    },
  });
  return {
    ...mutation,
    recovery: recovery?.ownerId === me?.id ? recovery : null,
    canEdit,
    returnToEditor: () => {
      if (!canEdit || mutation.isPending || pending.current?.ownerId !== me?.id)
        return;
      editing.current = true;
      setRecovery(null);
      setCanEdit(false);
    },
    ready: !!me,
  };
}
